import { z } from "zod";

/**
 * Deck (Metro-tiled touch view) layout — stored in the user profile
 * (users.prefs JSON). The grid is 12×8, tiles do not overlap.
 * Spec: docs/superpowers/specs/2026-07-13-deck-touch-view-design.md
 */
export const DECK_GRID_COLS = 12;
export const DECK_GRID_ROWS = 8;

export const deckTileSchema = z.object({
  /** Tile instance id (a free-form nanoid-like string, unique within the layout). */
  id: z.string().min(1),
  /** Widget type:"clock" | "score" | "overlay:<modeId>" | "events:home" | ... */
  widget: z.string().min(1),
  x: z.number().int().min(0).max(DECK_GRID_COLS - 1),
  y: z.number().int().min(0).max(DECK_GRID_ROWS - 1),
  w: z.number().int().min(1).max(DECK_GRID_COLS),
  h: z.number().int().min(1).max(DECK_GRID_ROWS),
});
export type DeckTile = z.infer<typeof deckTileSchema>;

export const deckLayoutSchema = z.object({
  version: z.literal(1),
  tiles: z.array(deckTileSchema).max(64),
});
export type DeckLayout = z.infer<typeof deckLayoutSchema>;

/** All profile settings. passthrough — so future keys are not lost. */
export const userPrefsSchema = z
  .object({ deckLayout: deckLayoutSchema.optional() })
  .passthrough();
export type UserPrefs = z.infer<typeof userPrefsSchema>;

/** Structural check: bounds + no overlap + id/widget uniqueness.
 *  A reason string on error, otherwise null (the grid.ts validatePlacements style). */
export function validateDeckTiles(tiles: DeckTile[]): string | null {
  const ids = new Set<string>();
  for (const t of tiles) {
    if (ids.has(t.id)) return `duplicate tile id: ${t.id}`;
    ids.add(t.id);
    if (t.x + t.w > DECK_GRID_COLS || t.y + t.h > DECK_GRID_ROWS) {
      return `tile out of bounds: ${t.id}`;
    }
  }
  for (let i = 0; i < tiles.length; i++) {
    for (let j = i + 1; j < tiles.length; j++) {
      const a = tiles[i]!;
      const b = tiles[j]!;
      const overlap =
        a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
      if (overlap) return `tiles overlap: ${a.id} / ${b.id}`;
    }
  }
  return null;
}
